<template>
  <div class="dashboard-container">
    <div class="header">
      <div class="header-left">
        <div class="back-home-btn" @click="goBack">
          <span class="back-icon">←</span>
          <span class="back-text">返回主页</span>
        </div>
        <div class="project-date-group">
          <input
            type="month"
            v-model="selectedMonth"
            @change="onMonthChange"
            class="date-picker month-picker"
          />
        </div>
        <button class="export-btn" :disabled="exporting" @click="handleExport">
          {{ exporting ? '导出中...' : '导出 Excel' }}
        </button>
      </div>

      <div class="title">海康智慧电梯监测月调度</div>

      <div class="header-time">
        <span class="system-time">系统时间：{{ currentTime }}</span>
      </div>
    </div>

    <div class="page-content" :class="{ loading: loading }">
      <div class="bottom-section">
        <div class="panel-card bottom-card events-panel">
          <div class="chart-title">上月历史触发（{{ lastMonthLabel }}）</div>
          <DailyEventAnalysis
            :chart-data="lastMonthChartData"
            :events="lastMonthEvents"
            :year-stats="lastMonthEventStats"
            :stat-labels="lastMonthStatLabels"
            enable-project-search
          />
        </div>

        <div class="panel-card bottom-card events-panel">
          <div class="chart-title">本月历史触发统计（{{ thisMonthLabel }}）</div>
          <DailyEventAnalysis
            :chart-data="thisMonthChartData"
            :events="thisMonthEvents"
            :year-stats="thisMonthEventStats"
            :stat-labels="thisMonthStatLabels"
            enable-project-search
          />
        </div>
      </div>

      <div class="month-dispatch-section bottom-section">
        <div class="panel-card month-left-panel">
          <div class="tab-buttons">
            <div
              v-for="tab in auditTabs"
              :key="tab.key"
              class="tab-button"
              :class="{ active: activeStatsTab === tab.key }"
              @click="activeStatsTab = tab.key"
            >
              {{ tab.label }}
            </div>
          </div>

          <div class="tab-content">
            <div class="list-container">
              <div v-if="!activeAuditList.length" class="empty-tip">暂无数据</div>
              <div v-else class="data-table">
                <div class="table-header">
                  <div class="header-item col-index">序号</div>
                  <div class="header-item flex-2">项目地址</div>
                  <div class="header-item col-device-code">设备代码</div>
                  <div class="header-item col-audit-times">年审时间</div>
                  <div class="header-item col-inspection-org">检验机构</div>
                </div>
                <div class="table-body stats-table-body">
                  <div
                    class="table-row"
                    v-for="(item, idx) in activeAuditList"
                    :key="activeStatsTab + '-' + idx"
                  >
                    <div class="table-cell col-index">{{ idx + 1 }}</div>
                    <div class="table-cell flex-2" :title="item.name">{{ item.name || '-' }}</div>
                    <div class="table-cell col-device-code" :title="item.deviceCode">{{ item.deviceCode || '-' }}</div>
                    <div class="table-cell col-audit-times">{{ item.auditTimes || '-' }}</div>
                    <div class="table-cell col-inspection-org" :title="formatInspectionOrg(item.inspectionOrg)">
                      {{ formatInspectionOrg(item.inspectionOrg) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-card month-right-panel">
          <div class="panel-header-spacer" aria-hidden="true"></div>
          <div class="manual-notes-body">
            <div class="manual-note-block" v-for="field in manualNoteFields" :key="field.key">
              <div class="chart-title">{{ field.label }}</div>
              <textarea
                v-if="!hasMonthSafetyReportSubmitted"
                v-model="manualInputs[field.key]"
                class="manual-textarea"
                :placeholder="`请输入${field.label}`"
              />
              <div v-else class="manual-readonly">{{ manualInputs[field.key] || '-' }}</div>
            </div>
          </div>
          <div v-if="!hasMonthSafetyReportSubmitted" class="manual-submit-bar">
            <button
              class="export-btn submit-report-btn"
              :disabled="submitting || !selectedProjectId"
              @click="submitMonthSafetyReport"
            >
              {{ submitting ? '提交中...' : '提交' }}
            </button>
          </div>
        </div>
      </div>

      <div class="bottom-section work-order-section">
        <WorkOrderRecords
          :title="`上月工单未处理记录（${lastMonthLabel}）`"
          :records="lastMonthWorkOrderRecords"
          :show-project-address="true"
          hide-status
          :container-style="{ flex: '1', minHeight: '200px' }"
        />
        <WorkOrderRecords
          :title="`本月工单未处理记录（${thisMonthLabel}）`"
          :records="thisMonthWorkOrderRecords"
          :show-project-address="true"
          hide-status
          :container-style="{ flex: '1', minHeight: '200px' }"
        />
      </div>

      <div class="panel-card">
        <div class="chart-title">签字确认</div>
        <div class="signature-grid" :class="{ 'single-sign': staffSignList.length === 1 }">
          <div
            class="signature-block"
            v-for="(sign, index) in staffSignList"
            :key="'sign-' + index"
          >
            <span class="info-label">{{ sign.title }}</span>
            <div class="signature-box">
              <img
                v-if="sign.staffImage"
                :src="sign.staffImage"
                :alt="sign.title"
                class="signature-image"
                @error="onStaffSignImageError(index)"
              />
              <span v-else class="signature-placeholder">暂无签名</span>
            </div>
          </div>
        </div>
        <div v-if="!staffSignList.length" class="empty-tip">暂无签字信息</div>
      </div>
    </div>
  </div>
</template>

<script>
import WorkOrderRecords from '@/components/WorkOrderRecords.vue'
import DailyEventAnalysis from '@/components/DailyEventAnalysis.vue'
import { dashboardAPI } from '@/api/dashboard'
import { exportMonthlyInspectionReport } from '@/utils/exportExcel'
import {
  buildDailyEventChartData,
  buildDailyEventSummaryText,
  buildEventCountsFromEvents,
  buildEventStatsFromCounts,
  parseLastMonthEventStats,
  parseThisMonthEventStats,
  parseLastMonthEventStat,
  parseTodayEventStat
} from '@/config/dailyEventConfig'
import {
  dateToMonthInput,
  monthInputToDate,
  getMonthRange,
  getLastMonthRange,
  isDateInRange,
  formatDate
} from '@/utils/monthHelper'

export default {
  name: 'MonthlyInspectionView',
  components: { WorkOrderRecords, DailyEventAnalysis },
  data() {
    return {
      loading: false,
      exporting: false,
      submitting: false,
      monthSafetyReport: null,
      currentTime: '',
      timer: null,
      selectedMonth: dateToMonthInput(new Date()),
      selectedProjectId: '',
      communityList: [],
      staffSignList: [],
      lastMonthEvents: [],
      thisMonthEvents: [],
      lastMonthEventCounts: {},
      thisMonthEventCounts: {},
      lastMonthEventStats: {
        thisYear: 0,
        lastYear: 0,
        total: 0
      },
      thisMonthEventStats: {
        thisYear: 0,
        lastYear: 0,
        total: 0
      },
      oldEventStr: '',
      eventStr: '',
      lastMonthWorkOrderRecords: [],
      thisMonthWorkOrderRecords: [],
      monthAuditList: [],
      monthShiyan125List: [],
      nextMonthAuditList: [],
      nextShiyan125List: [],
      activeStatsTab: 'monthAudit',
      auditTabs: [
        { key: 'monthAudit', label: '当月电梯年审项目' },
        { key: 'monthShiyan125', label: '当月125%载荷试验项目' },
        { key: 'nextMonthAudit', label: '下月电梯年审项目' },
        { key: 'nextShiyan125', label: '下月125%载荷试验项目' }
      ],
      manualInputs: {
        workMeetingNotes: '',
        elevatorInspectionPlan: '',
        safetyHazardInspection: '',
        safetyPromotion: ''
      },
      manualNoteFields: [
        { key: 'workMeetingNotes', label: '工作部署会议记录' },
        { key: 'elevatorInspectionPlan', label: '重点电梯排查计划' },
        { key: 'safetyHazardInspection', label: '安全隐患排查' },
        { key: 'safetyPromotion', label: '安全宣传知识' }
      ],
      lastMonthStatLabels: {
        first: '上月累计触发事件',
        second: '上上月累计触发事件',
        third: '总触发事件数'
      },
      thisMonthStatLabels: {
        first: '本月累计触发事件',
        second: '上月累计触发事件',
        third: '总触发事件数'
      }
    }
  },
  computed: {
    monthRange() {
      const anchor = monthInputToDate(this.selectedMonth)
      return getMonthRange(anchor)
    },
    lastMonthRange() {
      const anchor = monthInputToDate(this.selectedMonth)
      return getLastMonthRange(anchor)
    },
    formattedMonthRange() {
      const { start, end } = this.monthRange
      return `${start} 至 ${end}`
    },
    lastMonthLabel() {
      const { start, end } = this.lastMonthRange
      return `${start} ~ ${end}`
    },
    thisMonthLabel() {
      const { start, end } = this.monthRange
      return `${start} ~ ${end}`
    },
    lastMonthChartData() {
      return buildDailyEventChartData(this.lastMonthEventCounts)
    },
    thisMonthChartData() {
      return buildDailyEventChartData(this.thisMonthEventCounts)
    },
    thisMonthEventStatsForExport() {
      return buildEventStatsFromCounts(this.thisMonthEventCounts)
    },
    lastMonthSummaryText() {
      return this.oldEventStr || buildDailyEventSummaryText(this.lastMonthEventCounts)
    },
    thisMonthSummaryText() {
      return this.eventStr || buildDailyEventSummaryText(this.thisMonthEventCounts)
    },
    activeAuditList() {
      const listMap = {
        monthAudit: this.monthAuditList,
        monthShiyan125: this.monthShiyan125List,
        nextMonthAudit: this.nextMonthAuditList,
        nextShiyan125: this.nextShiyan125List
      }
      return listMap[this.activeStatsTab] || []
    },
    hasMonthSafetyReportSubmitted() {
      return !!(this.monthSafetyReport && typeof this.monthSafetyReport === 'object')
    }
  },
  created() {
    this.startClock()
    this.initPage()
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    formatDate,

    formatDateTime(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    },

    startClock() {
      this.currentTime = this.formatDateTime(new Date())
      this.timer = setInterval(() => {
        this.currentTime = this.formatDateTime(new Date())
      }, 1000)
    },

    goBack() {
      this.$router.push({ name: 'home' })
    },

    onMonthChange() {
      this.loadProjectData()
    },

    async initPage() {
      await this.loadCommunityList()
      const routeId = this.$route.query.id
      if (routeId) {
        this.selectedProjectId = String(routeId)
      } else if (this.communityList.length) {
        this.selectedProjectId = String(this.communityList[0].id)
      }
      if (this.selectedProjectId) {
        await this.loadProjectData()
      }
    },

    async loadCommunityList() {
      try {
        const cached = localStorage.getItem('communityData')
        if (cached) {
          this.communityList = JSON.parse(cached)
          if (this.communityList.length) return
        }
        const today = new Date()
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const response = await dashboardAPI.getScreenData({
          starttime: formatDate(startOfMonth),
          endtime: formatDate(today)
        })
        const data = response.data || response
        if (data.community_data && Array.isArray(data.community_data)) {
          this.communityList = data.community_data.map((item) => ({
            id: item.id,
            name: item.name || ''
          }))
        }
      } catch (e) {
        console.error('加载项目列表失败', e)
      }
    },

    async loadProjectData() {
      if (!this.selectedProjectId) return
      this.loading = true
      try {
        const { start, end } = this.monthRange
        const response = await dashboardAPI.getScreenReportMonth({
          id: this.selectedProjectId,
          starttime: start,
          endtime: end
        })
        const data = this.getReportData(response)
        this.applyEvents(data)
        this.applyMonthStats(data)
        this.applyWorkOrders(data)
        this.applySignatures(data)
        this.applyMonthSafetyReport(data)
      } catch (e) {
        console.error('加载月调度数据失败', e)
        this.$message && this.$message.error('加载月调度数据失败')
      } finally {
        this.loading = false
      }
    },

    getReportData(response) {
      if (!response) return {}
      const root = response.data !== undefined ? response.data : response
      if (root && typeof root === 'object' && root.data && typeof root.data === 'object') {
        return root.data
      }
      return root || {}
    },

    normalizeEvents(list) {
      if (!Array.isArray(list)) return []
      return list.map((item) => ({
        createtime: item.createtime || '',
        event_str: item.event_str || item.event_text || '',
        event_name: item.event_name || '',
        event_code: item.event || item.event_code,
        community_name: item.community_name || '',
        picture_file: item.picture_file || item.img || item.image || ''
      }))
    },

    applyEvents(data) {
      this.oldEventStr = data.old_event_str || ''
      this.eventStr = data.event_str || ''

      this.lastMonthEventStats = parseLastMonthEventStats(data)
      this.thisMonthEventStats = parseThisMonthEventStats(data)

      if (Array.isArray(data.last_month_event_stat)) {
        const parsed = parseLastMonthEventStat(data.last_month_event_stat)
        this.lastMonthEventCounts = parsed.counts
        this.lastMonthEvents = parsed.todayEvents
      } else {
        this.applyLegacyMonthEvents(data, 'last')
      }

      if (Array.isArray(data.today_event_stat)) {
        const parsed = parseTodayEventStat(data.today_event_stat)
        this.thisMonthEventCounts = parsed.counts
        this.thisMonthEvents = parsed.todayEvents
      } else {
        this.applyLegacyMonthEvents(data, 'this')
      }
    },

    applyLegacyMonthEvents(data, period) {
      const lastRange = this.lastMonthRange
      const thisRange = this.monthRange

      const lastMonthFromApi = this.normalizeEvents(
        data.last_month_event_data ||
          data.lastMonthEventData ||
          []
      )
      const thisMonthFromApi = this.normalizeEvents(
        data.this_month_event_data ||
          data.thisMonthEventData ||
          data.month_event_data ||
          []
      )

      if (period === 'last') {
        if (lastMonthFromApi.length) {
          this.lastMonthEvents = lastMonthFromApi
          this.lastMonthEventCounts = buildEventCountsFromEvents(lastMonthFromApi)
          return
        }

        const all = this.normalizeEvents(data.event_data || [])
        this.lastMonthEvents = all.filter((evt) =>
          isDateInRange(evt.createtime, lastRange.start, lastRange.end)
        )
        this.lastMonthEventCounts = buildEventCountsFromEvents(this.lastMonthEvents)
        return
      }

      if (thisMonthFromApi.length) {
        this.thisMonthEvents = thisMonthFromApi
        this.thisMonthEventCounts = buildEventCountsFromEvents(thisMonthFromApi)
        return
      }

      const all = this.normalizeEvents(data.event_data || [])
      this.thisMonthEvents = all.filter((evt) =>
        isDateInRange(evt.createtime, thisRange.start, thisRange.end)
      )
      this.thisMonthEventCounts = buildEventCountsFromEvents(this.thisMonthEvents)
    },

    parseInspectionOrderList(source) {
      if (!source) return []
      if (Array.isArray(source)) return source.filter((item) => item && typeof item === 'object')
      if (typeof source === 'object') return [source]
      return []
    },

    mapWorkOrderRecord(item) {
      return {
        id: item.id ?? '',
        areaId: item.area_id ?? '',
        areaSiteId: item.area_site_id ?? '',
        code: item.code ?? '',
        createTime: item.createtime ?? item.create_time ?? item.createTime ?? '',
        completeTime: item.completetime ?? '',
        eventId: item.eventid ?? '',
        urgency: item.level_text ?? item.level ?? '',
        orderType: item.order_type_text ?? item.order_type ?? '',
        repairContent: item.repair_content ?? '',
        repairImage: item.repair_images ?? '',
        staffId: item.staff_id ?? '',
        staffImage: item.staff_image ?? '',
        responsibleEmployee: item.repair_staff_id ?? '',
        status: item.status_text ?? item.status ?? '',
        title: item.title ?? '',
        orderImage: item.images ?? '',
        reportEmployee: item.staff_id ?? '',
        orderEvent: item.content ?? '',
        communityAddress: item.area_id ?? ''
      }
    },

    applyMonthStats(data) {
      this.monthAuditList = this.normalizeAuditList(data.month_audit)
      this.monthShiyan125List = this.normalizeAuditList(data.shiyan_125)
      this.nextMonthAuditList = this.normalizeAuditList(data.next_month_audit)
      this.nextShiyan125List = this.normalizeAuditList(data.next_shiyan_125)
    },

    normalizeAuditList(list) {
      if (!Array.isArray(list)) return []
      return list.map((item) => ({
        name: item.name || '',
        deviceCode: item.device_code || '',
        auditTimes: item.audittimes || '',
        inspectionOrg: item.inspection_organization || ''
      }))
    },

    formatInspectionOrg(value) {
      if (!value) return '-'
      const text = String(value)
      return text.length > 2 ? text.slice(0, 2) : text
    },

    applyWorkOrders(data) {
      const lastList = this.parseInspectionOrderList(data.last_inspection_order)
      const thisList = this.parseInspectionOrderList(data.inspection_order)
      this.lastMonthWorkOrderRecords = lastList.map((item) => this.mapWorkOrderRecord(item))
      this.thisMonthWorkOrderRecords = thisList.map((item) => this.mapWorkOrderRecord(item))
    },

    applySignatures(data) {
      this.staffSignList = this.parseStaffSignList(data.staff_sign)
    },

    createDefaultManualInputs() {
      return {
        workMeetingNotes: '',
        elevatorInspectionPlan: '',
        safetyHazardInspection: '',
        safetyPromotion: ''
      }
    },

    applyMonthSafetyReport(data) {
      const report = data?.month_safety_report
      if (report && typeof report === 'object') {
        this.monthSafetyReport = report
        this.manualInputs = {
          workMeetingNotes: report.meeting_record || '',
          elevatorInspectionPlan: report.elevator_plan || '',
          safetyHazardInspection: report.hazard_inspection || '',
          safetyPromotion: report.safety_publicity || ''
        }
        return
      }

      this.monthSafetyReport = null
      this.manualInputs = this.createDefaultManualInputs()
    },

    async submitMonthSafetyReport() {
      if (!this.selectedProjectId) {
        this.$message && this.$message.warning('未选择项目，无法提交')
        return
      }

      this.submitting = true
      try {
        const response = await dashboardAPI.submitMonthSafetyReport({
          community_id: this.selectedProjectId,
          month: this.selectedMonth,
          meeting_record: this.manualInputs.workMeetingNotes,
          elevator_plan: this.manualInputs.elevatorInspectionPlan,
          hazard_inspection: this.manualInputs.safetyHazardInspection,
          safety_publicity: this.manualInputs.safetyPromotion
        })

        if (response.code === 1) {
          this.$message && this.$message.success('提交成功')
          await this.loadProjectData()
        } else {
          this.$message && this.$message.error(response.msg || '提交失败')
        }
      } catch (e) {
        console.error('提交月调度安全报告失败', e)
        this.$message && this.$message.error('提交失败')
      } finally {
        this.submitting = false
      }
    },

    parseStaffSignList(staffSign) {
      if (!staffSign) return []

      const fallbackTitles = ['安全总监（手写签名）', '安全负责人（手写签名）']
      const list = Array.isArray(staffSign) ? staffSign : [staffSign]

      return list
        .filter((item) => item && typeof item === 'object')
        .map((item, index) => ({
          title:
            item.title ||
            item.staff_type_text ||
            item.type_text ||
            fallbackTitles[index] ||
            '手写签名',
          staffImage: this.firstImageUrl(item.staff_image || '')
        }))
    },

    firstImageUrl(value) {
      if (!value) return ''
      return String(value).split(',')[0].trim()
    },

    formatEventDisplay(evt) {
      if (evt.event_str) return evt.event_str
      if (evt.event_name) return evt.event_name
      return '-'
    },

    onStaffSignImageError(index) {
      if (this.staffSignList[index]) {
        this.$set(this.staffSignList[index], 'staffImage', '')
      }
    },

    getProjectName() {
      const item = this.communityList.find((c) => String(c.id) === String(this.selectedProjectId))
      return item?.name || '项目'
    },

    async handleExport() {
      this.exporting = true
      try {
        const { start, end } = this.monthRange
        const filename = `海康智慧电梯监测月调度_${this.getProjectName()}_${start}_${end}.xlsx`
        await exportMonthlyInspectionReport({
          formattedWeekRange: this.formattedMonthRange,
          currentTime: this.currentTime,
          showDateInTitle: false,
          lastWeekLabel: this.lastMonthLabel,
          thisWeekLabel: this.thisMonthLabel,
          oldEventStr: this.lastMonthSummaryText,
          lastWeekEvents: this.lastMonthEvents,
          eventStr: this.thisMonthSummaryText,
          thisWeekEvents: this.thisMonthEvents,
          thisWeekEventStats: this.thisMonthEventStatsForExport,
          thisPeriodTotalLabel: '本月累计触发事件',
          nextMonthAuditList: this.nextMonthAuditList,
          monthAuditList: this.monthAuditList,
          monthShiyan125List: this.monthShiyan125List,
          nextShiyan125List: this.nextShiyan125List,
          manualInputs: this.manualInputs,
          manualNoteFields: this.manualNoteFields,
          lastWeekWorkOrderRecords: this.lastMonthWorkOrderRecords,
          thisWeekWorkOrderRecords: this.thisMonthWorkOrderRecords,
          staffSignList: this.staffSignList,
          formatEventDisplay: (evt) => this.formatEventDisplay(evt),
          formatInspectionOrg: (value) => this.formatInspectionOrg(value),
          filename
        })
        this.$message && this.$message.success('导出成功')
      } catch (e) {
        console.error('导出失败', e)
        this.$message && this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped lang="less">
@import './weeklyInspectionStyles.less';
</style>
